import { Button } from './Button';

/**
 * 🔴 A FAILED LOAD SAYS SO, OFFERS A WAY ON, AND GIVES SUPPORT SOMETHING TO FIND — design-system plan DS-6a (5.1,
 * 5.6; F503, F70, F58, F613).
 *
 * No list or load failure offered a retry (about 38 screens could only be left and re-entered), and none gave the
 * person anything to quote. An `ErrorState` is `role="alert"` and carries:
 * - the heading — what did not happen ("Could not load invoices");
 * - the server's own sentence, which says why;
 * - `Try again`, when the screen can ask again (`onRetry`);
 * - `Reference: …` — the failed request's id (`X-Request-Id`, DS-6a), the same id the server logged and the audit
 *   trail holds, so "which request was it?" has an answer.
 * It is never drawn as an empty list: a list that did not load is not a list with nothing in it.
 */
export interface Failure {
  message?: string;
  detail?: string;
  requestId?: string | null;
}

/**
 * The sentence and the reference of anything thrown. A refusal from the server (an ApiError, PortalApiError or
 * SystemApiError — anything with a numeric `status`) carries a sentence written for people; any other Error is a
 * program's own message ("Cannot read properties of undefined"), which is not shown: a plain sentence is, and the
 * program's words go to the console (DS-6a review).
 */
export const FAILED_TO_DRAW = 'Something went wrong while drawing this. Nothing you saved is lost.';

export function describeFailure(error: unknown): { sentence: string; reference: string | null } {
  if (typeof error === 'string') return { sentence: error, reference: null };
  const e = (error ?? {}) as Failure & { status?: unknown };
  const fromServer = typeof e.status === 'number';
  if (!fromServer && error instanceof Error) console.error('Workbook:', error);
  const sentence = (fromServer ? e.detail || e.message : undefined) || FAILED_TO_DRAW;
  return { sentence, reference: typeof e.requestId === 'string' && e.requestId ? e.requestId : null };
}

/**
 * A lazy screen's code that did not arrive — a release replaced it while the page was open, or the connection dropped.
 * Try again cannot work there; Reload does (DS-6a review).
 */
export function isChunkFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(message);
}

/**
 * 🔴 A REFUSAL FOR WANT OF A PERMISSION SAYS WHO CAN FIX IT — design-system DS-11a4 (plan §1.6, C9.4; F624).
 *
 * The staff guard names the grant it wanted: *"This action requires the 'x' or 'y' permission."*
 * (`src/common/guards/permissions.guard.ts`). Shown as it was, with Try again, it offered a button that cannot succeed
 * and a sentence in the server's terms. Since DS-11a4 the nav hides what the person may not read, so somebody who
 * still lands here followed a bookmark or a shared link: they get "Ask an administrator of this company", the key on
 * a muted line for that administrator, and no Try again.
 *
 * ⚠️ THE BACK OFFICE ONLY (plan C9.4). "An administrator of this company" is the wrong advice for a dealer, and the
 * portal and the console share this component. So the advice is switched on by the back office alone — once, by its
 * `App.tsx` — rather than by a prop at every one of the screens that draw an `ErrorState`; the portal and the console
 * never call this and keep the server's own sentence.
 */
let adviseOnRefusals = false;
export const adviseOnRefusal = (on: boolean): void => {
  adviseOnRefusals = on;
};

/** The guard's sentence (`permissions.guard.ts`): the keys, `'a'` or `'a' or 'b' …`. */
const REFUSED = /requires the ('[^']+'(?: or '[^']+')*) permission/;

export function ErrorState({
  title,
  error,
  onRetry,
  retrying = false,
  retryLabel = 'Try again',
}: {
  title: string;
  error: unknown;
  onRetry?: () => void;
  retrying?: boolean;
  retryLabel?: string;
}) {
  const { sentence, reference } = describeFailure(error);
  const refused = adviseOnRefusals && (error as { status?: unknown } | null)?.status === 403 ? REFUSED.exec(sentence) : null;
  return (
    <div className="wb-error-state" role="alert">
      <div className="wb-error-state__title">{title}</div>
      <div className="wb-error-state__body">
        {refused ? 'You do not have access to this. Ask an administrator of this company to grant it.' : sentence}
      </div>
      {refused ? <div className="wb-error-state__reference">Permission needed: {refused[1]!.replace(/'/g, '')}</div> : null}
      {onRetry && !refused ? (
        <div className="wb-error-state__action">
          <Button size="sm" busy={retrying} onClick={onRetry}>
            {retryLabel}
          </Button>
        </div>
      ) : null}
      {reference ? <div className="wb-error-state__reference">Reference: {reference}</div> : null}
    </div>
  );
}
